import { Data, Model } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../DomainTypes';
import IssuerPagedLookupCriteria from '../Models/Issuers/Lookups/IssuerPagedLookupCriteria';
import IssuerPagedLookup from '../Models/Issuers/Lookups/IssuerPagedLookup';
import Issuer from '../Models/Issuers/Issuer';
import IssuerLookup from '../Models/Issuers/Lookups/IssuerLookup';

export interface IIssuerApiClient {

    /**
     * Gets the issuer for a given issuer id
     * @param issuerId The issuer's Id
     * @returns A
     */
    getIssuer(issuerId: number): AxiosPromise<Model.PlainTrackedObject<Issuer>>;

    /**
     * Gets a paged list of issuer lookups
     * @param request The page request containing filter criteria
     * @returns A paged list of
     */
    getIssuerLookupsPaged(request: Model.PartialPlainObject<Data.PageRequest<IssuerPagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<IssuerPagedLookup>>>;

    /**
     * Gets a list of issuers that match the search text. Limited to 50 results.
     */
    getIssuerLookups(searchText: string): AxiosPromise<Array<Model.PlainObject<IssuerLookup>>>;

    /** 
     * Gets an issuer that matches the id.
     */
    getIssuerLookup(issuerId: number): AxiosPromise<Model.PlainObject<IssuerLookup>>;

    /**
     * Saves an issuer
     * @param issuer Issuer to be saved
     * @returns A  representing the result of the asynchronous operation.
     */
    saveIssuer(issuer: Model.PartialPlainObject<Issuer>): AxiosPromise<Model.PlainTrackedObject<Issuer>>;

    // Client only properties / methods
}

@injectable()
export default class IssuerApiClient extends Data.ApiClientBase implements IIssuerApiClient {

    constructor (config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/issuer`);
    }

    public getIssuer(issuerId: number): AxiosPromise<Model.PlainTrackedObject<Issuer>> {
        return this.axios.get(`${this.apiPath}/${issuerId}`);
    }

    public getIssuerLookupsPaged(request: Model.PartialPlainObject<Data.PageRequest<IssuerPagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<IssuerPagedLookup>>> {
        return this.axios.post(`${this.apiPath}/issuers-paged`, request);
    }

    public getIssuerLookups(searchText: string): AxiosPromise<Array<Model.PlainObject<IssuerLookup>>> {
        return this.axios.get(`${this.apiPath}/lookups?searchText=${encodeURIComponent(searchText)}`);
    }

    public getIssuerLookup(issuerId: number): AxiosPromise<Model.PlainObject<IssuerLookup>> {
        return this.axios.get(`${this.apiPath}/lookup/${issuerId}`);
    }

    public saveIssuer(issuer: Model.PartialPlainObject<Issuer>): AxiosPromise<Model.PlainTrackedObject<Issuer>> {
        return this.axios.post(`${this.apiPath}/save-issuer`, issuer);
    }

    // Client only properties / methods
}